import React, { useState, useEffect, useRef } from "react";
import { Dropdown } from "primereact/dropdown";
import { Button } from "primereact/button";
import { plazaServices } from "../pages/tollPlaza/plazaReportService";
import NewUpload from "./NewUpload";
import JSZip from "jszip";
import imagineHomeService from "../pages/imagineHome/imagineHomeService";
import { ProgressBar } from "primereact/progressbar";

const MultiUpload = ({ serviceId, vendor, uploadType, skus, showToast }) => {
    const acceptedFormatExcel = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel";
    const acceptedFormatImages = ".png,.jpg,.jpeg,.zip";
    // const acceptedFormat3DModel = ".mtl,.obj,.zip";
    const acceptedFormat3DModel = ".glb,.dwg,.zip";

    const [errorMsg, setErrorMsg] = useState(null);
    const [infoMsg, setInfoMsg] = useState(null);
    const [fileCountText, setFileCountText] = useState("Select file(s)");
    const [images, setImages] = useState([]);
    const [modelFiles, setModelFiles] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [showProgressBar, setShowProgressBar] = useState(false);
    const [uplodedCount, setUploadedCount] = useState(0);
    const [totalUploadCount, setTotalUploadCount] = useState(0);
    const onReset = () => {
        setErrorMsg(null);
        setImages([]);
        setModelFiles([]);
        setFileCountText(getFileCountText(null));
        setInfoMsg(null);
        setShowProgressBar(false);
        setUploadedCount(0);
        setTotalUploadCount(null);
    };
    useEffect(() => {
        setErrorMsg(null);
        setImages([]);
        setModelFiles([]);
    }, [skus]);
    const getFileCountText = (totalFileCount) => {
        return !totalFileCount ? "Select file(s)" : `${totalFileCount} file${totalFileCount > 1 ? "s" : ""} selected`;
    };
    const excludeFile = (zip, filename) => {
        // skip hidden/system files like __MACOSX/ or .DS_Store
        return filename.startsWith("__MACOSX/") || filename.startsWith(".") || zip.files[filename].dir;
    };
    const handleChange = async (e) => {
        onReset();
        const files = Array.from(e.target.files);

        if (!files || files.length === 0) return;
        const totalFileCount = files.length;
        console.log(totalFileCount);
        if (uploadType === "images") {
            let imageFiles = [];

            const { error, data } = await validateFilesforImagesUpload(files);
            if (error) {
                setErrorMsg(error);
            } else {
                imageFiles = data;
                setImages(imageFiles);
                setTotalUploadCount(imageFiles.length);
                setFileCountText(getFileCountText(totalFileCount));
            }
        } else if (uploadType === "3D_model") {
            let modelFiles = [];

            const { error, data } = await validateFilesFor3DModelUpload(files);
            if (error) {
                setErrorMsg(error);
            } else {
                modelFiles = data;
                setModelFiles(modelFiles);
                setTotalUploadCount(modelFiles.length);
                setFileCountText(getFileCountText(totalFileCount));
            }
        }
        // reset input so selecting the same file later still triggers change
        e.target.value = "";
    };

    const skuExists = (sku) => {
        const skuExists = skus.some((s) => s.value === sku);
        return skuExists;
    };
    const validateFilesforImagesUpload = async (files) => {
        // 1. Name each file with the product's SKU followed by an underscore(_). Example: 12345_front.jpg, 12345_side.png, 12345_back.jpg
        // OR
        // 2. Combine all images for a single product into a ZIP file. The ZIP file itself must be named using only the product's SKU. Example: 12345.zip
        const validExtensions = /\.(png|jpe?g)$/i;
        const results = [];

        for (const file of files) {
            const { name } = file;

            // check if ZIP
            if (name.toLowerCase().endsWith(".zip")) {
                // 1. extract SKU from zip name
                // validate filename format {SKU}.zip
                const match = name.match(/^([^_]+)\.zip$/i);
                if (!match) {
                    return { error: `Invalid zip filename format: ${name}. Must be {SKU}.zip` };
                }
                const sku = match[1];

                if (!skuExists(sku)) {
                    return { error: `SKU "${sku}" not found for file: ${name}.` };
                }

                // 2. read zip (using JSZip or similar lib)
                const zip = await JSZip.loadAsync(files[0]);
                const imageFiles = [];

                for (const [fileName, zipEntry] of Object.entries(zip.files)) {
                    // skip hidden/system files like __MACOSX/ or .DS_Store
                    if (excludeFile(zip, fileName)) {
                        continue;
                    }
                    if (!validExtensions.test(fileName)) {
                        return { error: `Invalid file type inside zip ${name}: ${fileName}. Only PNG/JPG/JPEG allowed.` };
                    }
                    const blob = await zipEntry.async("blob");
                    imageFiles.push(new File([blob], fileName, { type: blob.type }));
                }

                // 3. push into results
                results.push({ sku, images: imageFiles });
            } else {
                //  handle individual images
                if (!validExtensions.test(name)) {
                    return { error: `Invalid file type: ${name}. Only PNG/JPG/JPEG allowed.` };
                }

                const match = name.match(/^([^_]+)_.+\.(png|jpg|jpeg)$/i);
                if (!match) {
                    return { error: `Invalid filename format: ${name}. Must be {SKU}_filename.jpg|png|jpeg` };
                }
                const sku = match[1];

                if (!skuExists(sku)) {
                    return { error: `SKU "${sku}" not found for file: ${name}.` };
                }

                let skuEntry = results.find((r) => r.sku === sku);
                if (!skuEntry) {
                    skuEntry = { sku, images: [] };
                    results.push(skuEntry);
                }
                skuEntry.images.push(file);
            }
        }

        return { data: results }; // success
    };

    const validateFilesFor3DModelUpload = async (files) => {
        // We support two upload modes:
        // Single ZIP → contains multiple files named like {SKU}.glb or {SKU}.dwg.
        // Multiple files directly → each named {SKU}.glb or {SKU}.dwg.

        // Each SKU can only have one model file (.glb or .dwg).

        const validExtensions = /\.(glb|dwg)$/i;
        const results = new Map(); // sku -> file
        let errorObj = {};

        // helper to process a single file
        const processFile = (fileName, fileObj) => {
            if (!validExtensions.test(fileName)) {
                return { error: `Invalid file type: ${fileName}. Only GLB/DWG allowed.` };
            }
            const match = fileName.match(/^([^_]+)\.(glb|dwg)$/i);
            if (!match) {
                return { error: `Invalid filename format: ${fileName}. Must be {SKU}.glb|.dwg` };
            }
            const sku = match[1];

            if (!skuExists(sku)) {
                return { error: `SKU "${sku}" not found for file: ${fileName}` };
            }

            if (results.has(sku)) {
                return { error: `Duplicate model for SKU "${sku}". Only one GLB/DWG file allowed per SKU.` };
            }

            results.set(sku, fileObj);
            return {};
        };

        // case 1: single zip uploaded
        if (files.length === 1 && files[0].name.toLowerCase().endsWith(".zip")) {
            const zip = await JSZip.loadAsync(files[0]);

            for (const [fileName, zipEntry] of Object.entries(zip.files)) {
                 if (excludeFile(zip, fileName)) {
                    continue;
                }
                // Always strip folder path if present
                // This case occurs when zip files preserve folder structure. Some exports (like 3D tools) put files into a folder inside the zip, others don’t.
                const baseName = fileName.includes("/")
                    ? fileName.split("/").pop() // Case A: inside folder
                    : fileName; // Case B: plain filename

                if (!validExtensions.test(baseName)) {
                    return { error: `Invalid file type inside ${files[0].name}: ${baseName}. Only GLB/DWG allowed.` };
                }
                const blob = await zipEntry.async("blob");
                const fileObj = new File([blob], baseName, { type: blob.type });

                errorObj = processFile(baseName, fileObj);
                if (errorObj.error) return errorObj;
            }
        } else {
            // case 2: multiple files directly
            for (const file of files) {
                errorObj = processFile(file.name, file);
                if (errorObj.error) return errorObj;
            }
        }

        // final results as array
        return {
            data: Array.from(results, ([sku, file]) => ({ sku, model: file })),
        };
    };
    // const validateFilesFor3DModelUpload = async (files) => {
    //     const validExtensions = /\.(obj|mtl)$/i;
    //     const results = [];

    //     for (const file of files) {
    //         const { name } = file;

    //         // check if ZIP
    //         if (name.toLowerCase().endsWith(".zip")) {
    //             const match = name.match(/^([^_]+)_.+/);
    //             if (!match) {
    //                 return { error: `Invalid zip filename format: ${name}. Must be {SKU}_filename.zip` };
    //             }
    //             const sku = match[1];

    //             if (!skuExists(sku)) {
    //                 return { error: `SKU "${sku}" not found for file: ${name}. Filename format must be {SKU}_filename.zip` };
    //             }

    //             // extract with JSZip
    //             const zip = await JSZip.loadAsync(files[0]);
    //             const modelFiles = [];

    //             for (const [fileName, zipEntry] of Object.entries(zip.files)) {
    //                 if (!validExtensions.test(fileName)) {
    //                     return { error: `Invalid file type inside ${name}: ${fileName}. Only OBJ/MTL allowed.` };
    //                 }
    //                 const blob = await zipEntry.async("blob");
    //                 modelFiles.push(new File([blob], fileName, { type: blob.type }));
    //             }

    //             if (modelFiles.length !== 2) {
    //                 return { error: `Invalid number of files in ${name}. Each SKU must have exactly 1 .obj and 1 .mtl file.` };
    //             }

    //             const hasOBJ = modelFiles.some((f) => f.name.toLowerCase().endsWith(".obj"));
    //             const hasMTL = modelFiles.some((f) => f.name.toLowerCase().endsWith(".mtl"));
    //             if (!hasOBJ || !hasMTL) {
    //                 return { error: `Zip ${name} must contain both .obj and .mtl files.` };
    //             }

    //             results.push({ sku, models: modelFiles });
    //         } else {
    //             // handle standalone obj/mtl
    //             if (!validExtensions.test(name)) {
    //                 return { error: `Invalid file type: ${name}. Only OBJ/MTL allowed.` };
    //             }

    //             const match = name.match(/^([^_]+)_.+/);
    //             if (!match) {
    //                 return { error: `Invalid filename format: ${name}. Must be {SKU}_filename.obj/.mtl` };
    //             }
    //             const sku = match[1];

    //             if (!skuExists(sku)) {
    //                 return { error: `SKU "${sku}" not found for file: ${name}. Filename format must be {SKU}_filename.obj/.mtl` };
    //             }

    //             let skuEntry = results.find((r) => r.sku === sku);
    //             if (!skuEntry) {
    //                 skuEntry = { sku, models: [] };
    //                 results.push(skuEntry);
    //             }
    //             skuEntry.models.push(file);
    //         }
    //     }

    //     // final validation: each SKU must have 1 .obj and 1 .mtl
    //     for (const { sku, models } of results) {
    //         const hasOBJ = models.some((f) => f.name.toLowerCase().endsWith(".obj"));
    //         const hasMTL = models.some((f) => f.name.toLowerCase().endsWith(".mtl"));
    //         if (!(hasOBJ && hasMTL && models.length === 2)) {
    //             return { error: `SKU ${sku} must have exactly one .obj and one .mtl file.` };
    //         }
    //     }

    //     return { data: results }; // success
    // };

    const handleFileUpload = async (e) => {
        e.target.blur();
        setUploading(true);
        setUploadedCount(0);
        setShowProgressBar(true);
        setInfoMsg("Please keep this page open until the upload is complete.");
        try {
            const mediaFiles = uploadType === "images" ? images : modelFiles;
            for (const obj of mediaFiles) {
                const formData = new FormData();
                formData.append("serviceId", serviceId);
                formData.append("userID", localStorage.getItem("emailId"));
                formData.append("vendor", vendor);
                formData.append("sku", obj.sku);
                formData.append("type", uploadType);

                if (uploadType === "images") {
                    obj.images.forEach((file) => formData.append("images", file));
                } else {
                    // formData.append(
                    //     "obj_file",
                    //     obj.models.find((f) => f.name.toLowerCase().endsWith(".obj"))
                    // );
                    // formData.append(
                    //     "mtl_file",
                    //     obj.models.find((f) => f.name.toLowerCase().endsWith(".mtl"))
                    // );
                    // const imagesMedia = obj.models.filter((f) => !f.name.toLowerCase().endsWith(".mtl") && !f.name.toLowerCase().endsWith(".obj"));
                    // if (imagesMedia.length) {
                    //     imagesMedia.forEach((file) => formData.append("texture_3D_files", file));
                    // }
                    const modelFileKey = obj.model.name.toLowerCase().endsWith(".glb") ? "glb_file" : "dwg_file";
                    formData.append(modelFileKey, obj.model);
                }

                try {
                    const res = await imagineHomeService.uploadSKUMedia(formData);

                    if (res && res.data.status === 200) {
                        setUploadedCount((prev) => prev + 1);
                    } else {
                        showToast("error", "Error", res?.error?.message || `Error uploading file for SKU ${obj.sku}`);
                    }
                } catch (err) {
                    console.error("Error uploading:", err);
                    showToast("error", "Error", err?.message || `Error uploading file for SKU ${obj.sku}`);
                }
            }
            showToast("success", "Success", `Files uploaded successfully!`);
            onReset();
        } finally {
            setUploading(false);
        }
    };

    return (
        <div className="mt-3">
            {/* <p className="text-center mb-3 text-xl font-larken">Upload Excel File</p> */}
            {errorMsg ? (
                <p className="text-center text-sm font-Gerstner-ProgramRegular" style={{ color: "red" }}>
                    {errorMsg}
                </p>
            ) : null}
            {infoMsg ? (
                <p className="text-center font-bold text-lg font-Gerstner-ProgramRegular" style={{ color: "#f4a261" }}>
                    {infoMsg}
                </p>
            ) : null}
            {showProgressBar && (
                <div className="grid justify-content-center">
                    <div className="col-4">
                        <ProgressBar value={(uplodedCount / totalUploadCount) * 100} color="#9AAB89"></ProgressBar>
                        <label style={{ color: "#9AAB89", WebkitTextFillColor: "#9AAB89" }} className="mt-2 font-Gerstner-ProgramRegular text-center block">
                            <span className="font-bold text-md ">Uploading {uplodedCount}</span> <span className="text-sm">of {totalUploadCount}</span>
                        </label>
                    </div>
                </div>
            )}
            <div className="mt-3 grid justify-content-center align-items-center text-center gap-3">
                {/* Excel File Upload */}
                {/* <div className="col-12 sm:col-6 md:col-3">
                    <label className="flex flex-column align-items-center justify-content-center border-1 border-dashed border-round-md cursor-pointer border-700 text-center relative" style={{ padding: "0.5rem", backgroundColor: "#E5E1CF" }}>
                        {!excelFile ? (
                            <span className="font-Gerstner-ProgramRegular text-sm" style={{ color: "#333" }}>
                                Select Excel file
                            </span>
                        ) : (
                            <span className="font-Gerstner-ProgramRegular text-sm flex items-center gap-2" style={{ color: "#333" }}>
                                <span>{excelFile.name}</span>
                            </span>
                        )}
                        <input type="file" className="hidden" multiple={false} onChange={handleChangeExcel} accept={acceptedFormatExcel} />
                    </label>
                </div> */}

                {/* Model File Upload */}
                <div className="col-12 sm:col-6 md:col-3">
                    <label className="flex flex-column align-items-center justify-content-center border-1 border-dashed border-round-md cursor-pointer border-700 text-center" style={{ padding: "0.5rem", backgroundColor: "#E5E1CF" }}>
                        <span className="font-Gerstner-ProgramRegular text-sm" style={{ color: "#333" }}>
                            {fileCountText}
                        </span>
                        <input type="file" className="hidden" multiple={true} onChange={handleChange} accept={uploadType === "images" ? acceptedFormatImages : acceptedFormat3DModel} />
                    </label>
                </div>

                {/* Upload Button */}
                <div className="col-12 sm:col-6 md:col-3">
                    <Button
                        disabled={uploading || errorMsg || !skus || (uploadType === "images" && !images.length) || (uploadType === "3D_model" && !modelFiles.length)}
                        label={uploadType === "images" ? "UPLOAD IMAGE" : "UPLOAD 3D MODEL"}
                        className="p-button-rounded font-Gerstner-ProgramRegular charcoal-color w-full"
                        style={{
                            letterSpacing: "1.44px",
                            backgroundColor: "#9AAB89",
                            borderColor: "#9AAB89",
                        }}
                        onClick={(e) => handleFileUpload(e)}
                    />
                </div>
            </div>

            {/* <p className="text-center text-sm font-Gerstner-ProgramRegular">
                <span style={{ fontStyle: "italic", fontFamily: "Montserrat" }}>To download sample template. </span>
                <span>
                    <a href={`${process.env.REACT_APP_IMAGE_STATIC_URL}/ProductCatalogTemplate.xlsx`} className="charcoal-color font-semibold underline cursor-pointer font-Gerstner-ProgramRegular" download>
                        Click here
                    </a>
                </span>
            </p> */}
        </div>
    );
};

export default MultiUpload;
